// src/hooks/useFleetTracking.ts - FIXED: Pass through all raw telemetry data
import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Device } from '../types';

interface DeviceTelemetry {
  timestamp: string;
  location: {
    latitude: number;
    longitude: number;
  };
  speed?: number;
  battery?: number;
  engineRPM?: number;
  heading?: number;
  ignition?: boolean;
  isMoving?: boolean;
  fuelLevel?: number;
  temperature?: number;
  altitude?: number;
  signalStrength?: number;

  // ✅ Multi-protocol support
  state?: {
    reported?: {
      [key: string]: any;  // Teltonika AVL format
    };
  };
  acc_on?: boolean;         // JTT808
  speed_kmh?: number;       // JTT808
  instantaneous_speed_kmh?: number;  // JTT808

  [key: string]: any;  // Allow any additional fields
}

interface FleetDevice {
  device: Device;
  telemetry: DeviceTelemetry | null;
  lastUpdate?: string;
}

interface FleetUpdate {
  imei: string;
  deviceId: string;
  plateNumber: string;
  data: any;  // ✅ Changed to 'any' to accept all raw data
  location: {
    latitude: number;
    longitude: number;
    speed: number;
    heading: number;
    ignition: boolean;
    timestamp: string;
    isMoving: boolean;
  };
}

interface UseFleetTrackingReturn {
  fleetDevices: Map<string, FleetDevice>;
  isConnected: boolean;
  isSubscribed: boolean;
  error: string | null;
  stats: {
    total: number;
    online: number;
    moving: number;
    offline: number;
  };
  subscribeToFleet: () => void;
  unsubscribeFromFleet: () => void;
  reconnect: () => void;
  clearError: () => void;
  getDeviceByImei: (imei: string) => FleetDevice | undefined;
}

export const useFleetTracking = (): UseFleetTrackingReturn => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [fleetDevices, setFleetDevices] = useState<Map<string, FleetDevice>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimestamps = useRef<Map<string, number>>(new Map());

  socketRef.current = socket;

  // In useFleetTracking.ts — fix stats to use parsed speed, not isMoving flag
  const stats = {
    total: fleetDevices.size,
    online: Array.from(fleetDevices.values()).filter(d => {
      const tel = d.telemetry;
      if (!tel) return false;
      const lastSeen = new Date(tel.processed_at || tel.timestamp).getTime();
      return (Date.now() - lastSeen) < 5 * 60 * 1000; // within 5 min
    }).length,
    moving: Array.from(fleetDevices.values()).filter(d => {
      const tel = d.telemetry;
      if (!tel) return false;
      // Check both protocols
      const speed = tel.speed_kmh ?? tel.instantaneous_speed_kmh ?? tel.state?.reported?.sp ?? tel.speed ?? 0;
      return speed > 3;
    }).length,
    offline: Array.from(fleetDevices.values()).filter(d => {
      const tel = d.telemetry;
      if (!tel) return true;
      const lastSeen = new Date(tel.processed_at || tel.timestamp).getTime();
      return (Date.now() - lastSeen) >= 5 * 60 * 1000;
    }).length,
  };

  const subscribeToFleet = useCallback(() => {
    const currentSocket = socketRef.current;
    if (!currentSocket || !isConnected) {
      setError('Not connected to server');
      return;
    }

    console.log('Subscribing to fleet updates...');
    currentSocket.emit('subscribe_to_fleet');
  }, [isConnected]);

  const unsubscribeFromFleet = useCallback(() => {
    const currentSocket = socketRef.current;
    if (!currentSocket) return;

    console.log('Unsubscribing from fleet...');
    currentSocket.emit('unsubscribe_from_fleet');
    setIsSubscribed(false);
    setFleetDevices(new Map());
    lastUpdateTimestamps.current.clear();
  }, []);

  const reconnect = useCallback(() => {
    const currentSocket = socketRef.current;
    if (currentSocket) {
      console.log('Reconnecting...');
      setError('Reconnecting...');
      currentSocket.disconnect();
      setTimeout(() => currentSocket.connect(), 1000);
      setReconnectAttempt(prev => prev + 1);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getDeviceByImei = useCallback((imei: string): FleetDevice | undefined => {
    return fleetDevices.get(imei);
  }, [fleetDevices]);

  // ✅ FIXED: Process fleet update with ALL raw data
  const processFleetUpdate = useCallback((update: FleetUpdate) => {
    const updateTime = new Date(update.data.timestamp).getTime();
    const lastUpdate = lastUpdateTimestamps.current.get(update.imei) || 0;

    if (updateTime <= lastUpdate) {
      console.log(`⏭️ Skipping duplicate update for ${update.imei}`);
      return;
    }

    lastUpdateTimestamps.current.set(update.imei, updateTime);

    // Skip HEARTBEAT and UNKNOWN — no location or sensor data
if (update.data.message_type === 'HEARTBEAT' || 
  update.data.message_type === 'UNKNOWN') {
return;
}

    console.log(`✅ Processing update for ${update.imei}:`, {
      message_type: update.data.message_type,
      battery_voltage_V: update.data.battery_voltage_V,
      accOn: update.data.acc_on,
      speedKmh: update.data.speed_kmh
    });

    const hasValidLocation =
    update.location.latitude !== 0 &&
    update.location.longitude !== 0;
  
  setFleetDevices(prevMap => {
    if (!prevMap.has(update.imei)) return prevMap;
  
    const newMap = new Map(prevMap);
    const existingDevice = newMap.get(update.imei);
  
    if (existingDevice) {
      newMap.set(update.imei, {
        ...existingDevice,
        telemetry: {
          ...update.data,
          timestamp: update.data.timestamp,
          location: hasValidLocation
            ? { latitude: update.location.latitude, longitude: update.location.longitude }
            : existingDevice.telemetry?.location || { latitude: 0, longitude: 0 },
          speed: update.location.speed,
          heading: update.location.heading,
          ignition: update.location.ignition,
          isMoving: update.location.isMoving
        } as DeviceTelemetry,
        lastUpdate: update.data.timestamp,
        device: {
          ...existingDevice.device,
          status: update.data.isMoving ? 'online' : existingDevice.device.status,
          location: {
            type: 'Point',
            coordinates: [update.location.longitude, update.location.latitude]
          }
        }
      });
    }
  
    return newMap;
  });
}, []);  // ← closes processFleetUpdate useCallback

// Initialize socket connection
useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found');
      return;
    }

    let socketUrl = 'http://localhost';
    if (process.env.REACT_APP_API_URL) {
      socketUrl = process.env.REACT_APP_API_URL.replace('/api', '');
    }

    console.log('Connecting to Socket.IO at:', socketUrl);

    const socketInstance = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 20000
    });

    socketInstance.on('connect', () => {
      console.log('✅ Connected to fleet tracking server');
      setIsConnected(true);
      setError(null);
      setReconnectAttempt(0);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Disconnected:', reason);
      setIsConnected(false);
      setIsSubscribed(false);
      setError(`Disconnected: ${reason}`);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('🔴 Connection error:', error.message);
      setIsConnected(false);
      setError(`Connection failed: ${error.message}`);

      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectAttempt(prev => prev + 1);
      }, delay);
    });

    socketInstance.on('fleet_subscription_success', (data: {
      companyId: string;
      deviceCount: number;
      fleetData: Array<{
        device: Device;
        telemetry: any;  // ✅ Accept any telemetry structure
      }>;
    }) => {
      console.log(`✅ Fleet subscription successful: ${data.deviceCount} devices`);

      const devicesMap = new Map<string, FleetDevice>();

      data.fleetData.forEach(deviceData => {

        // nly track bound devices
        if (!deviceData.device.isBoundToVehicle) return;

        devicesMap.set(deviceData.device.imei, {
          device: deviceData.device,
          telemetry: deviceData.telemetry ? {
            ...deviceData.telemetry,  // Preserve all raw fields
            timestamp: deviceData.telemetry.timestamp,
            location: deviceData.telemetry.location
          } as DeviceTelemetry : null,
          lastUpdate: deviceData.telemetry?.timestamp || new Date().toISOString()
        });

        if (deviceData.telemetry?.timestamp) {
          lastUpdateTimestamps.current.set(
            deviceData.device.imei,
            new Date(deviceData.telemetry.timestamp).getTime()
          );
        }
      });

      setFleetDevices(devicesMap);
      setIsSubscribed(true);
      setError(null);
    });

    socketInstance.on('fleet_telemetry_batch', (data: {
      updates: FleetUpdate[];
      timestamp: string;
    }) => {
      console.log(`📦 Batch update received: ${data.updates.length} devices`);

      data.updates.forEach(update => {
        processFleetUpdate(update);
      });
    });

    socketInstance.on('fleet_telemetry_update', (update: FleetUpdate) => {
      console.log(`📍 Fleet update for ${update.plateNumber} (${update.imei})`);
      processFleetUpdate(update);
    });

    socketInstance.on('fleet_unsubscription_success', () => {
      console.log('✅ Fleet unsubscription successful');
      setIsSubscribed(false);
      setFleetDevices(new Map());
      lastUpdateTimestamps.current.clear();
    });

    socketInstance.on('error', (errorData: any) => {
      console.error('🔴 Socket error:', errorData);
      setError(errorData.message || 'Real-time connection error');
    });

    setSocket(socketInstance);

    return () => {
      console.log('🧹 Cleaning up fleet tracking...');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketInstance) {
        socketInstance.disconnect();
      }
      lastUpdateTimestamps.current.clear();
    };
  }, [processFleetUpdate]);

  useEffect(() => {
    if (isConnected && !isSubscribed) {
      console.log('🔄 Auto-subscribing to fleet...');
      subscribeToFleet();
    }
  }, [isConnected, isSubscribed, subscribeToFleet]);

  return {
    fleetDevices,
    isConnected,
    isSubscribed,
    error,
    stats,
    subscribeToFleet,
    unsubscribeFromFleet,
    reconnect,
    clearError,
    getDeviceByImei,
  };
};